import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';

const Contact = () => {
    const [status, setStatus] = useState('idle'); // idle, sending, sent, error

    const handleSubmit = (e) => {
        e.preventDefault();
        setStatus('sending');
        // Simulate network request
        setTimeout(() => {
            setStatus('sent');
        }, 2000);
    };

    return (
        <div className="min-h-screen bg-cyber-gray text-white font-rajdhani relative overflow-hidden flex items-center justify-center">
            {/* Background Grid Animation */}
            <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            </div>

            <div className="max-w-xl w-full mx-4 relative z-10">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-gray-900/80 backdrop-blur-md border border-gray-700 p-8 rounded-2xl shadow-2xl relative overflow-hidden"
                >
                    {/* Decorative Elements */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-neon-blue to-neon-purple"></div>
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-neon-blue rounded-full blur-[80px] opacity-20 pointer-events-none"></div>

                    <div className="mb-8 text-center">
                        <h1 className="text-3xl font-bold font-orbitron text-white mb-2">INITIATE TRANSMISSION</h1>
                        <p className="text-gray-400 text-sm">Establish a secure uplink to our support node.</p>
                    </div>

                    <AnimatePresence mode="wait">
                        {status === 'sent' ? (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="text-center py-12"
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 200, damping: 10 }}
                                    className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6"
                                >
                                    <CheckCircle className="w-10 h-10 text-green-500" />
                                </motion.div>
                                <h3 className="text-2xl font-bold font-orbitron text-white mb-2">TRANSMISSION RECEIVED</h3>
                                <p className="text-gray-400">Our operatives will decode your message shortly.</p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-8 text-sm text-neon-blue hover:text-white transition-colors underline"
                                >
                                    Send another transmission
                                </button>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onSubmit={handleSubmit}
                                className="space-y-6"
                            >
                                <div>
                                    <label className="block text-xs font-mono text-gray-500 uppercase mb-2">Identity Signature</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue transition-all"
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono text-gray-500 uppercase mb-2">Comms Channel</label>
                                    <input
                                        type="email"
                                        required
                                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue transition-all"
                                        placeholder="email@domain.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono text-gray-500 uppercase mb-2">Payload</label>
                                    <textarea
                                        required
                                        rows="4"
                                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue transition-all resize-none"
                                        placeholder="Enter message data..."
                                    ></textarea>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    disabled={status === 'sending'}
                                    className="w-full bg-gradient-to-r from-neon-blue to-blue-600 text-black font-bold font-orbitron py-4 rounded-lg flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(0,123,255,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {status === 'sending' ? (
                                        <>
                                            <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
                                            UPLOADING...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            TRANSMIT DATA
                                        </>
                                    )}
                                </motion.button>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </div>
    );
};

export default Contact;
